import { useState, useEffect, useCallback, useLayoutEffect } from "react"
import { cn } from "../../lib/utils"

/**
 * AnimatedPresence - A reusable wrapper that animates children in/out
 * with a scale + fade transition and smooth layout collapse.
 *
 * Uses a CSS grid row transition (0fr <-> 1fr) to smoothly collapse
 * layout space. An inner overflow container uses negative margin + padding
 * to preserve space for box-shadows and glow effects.
 *
 * @param {boolean} show - Whether the children should be visible
 * @param {number} duration - Transition duration in ms (default: 200)
 * @param {string} className - Additional CSS classes for the outer wrapper
 * @param {number} shadowPad - Padding in px to reserve for box-shadow overflow (default: 40)
 * @param {React.Ref} ref - Attached to the outer wrapper div; receives null
 *   while hidden (the component unmounts entirely), so callback refs can
 *   distinguish "not rendered" from "rendered somewhere off-screen"
 * @param {React.ReactNode} children - Content to animate
 */
function AnimatedPresence({ show, children, duration = 200, className, shadowPad = 40, ref }) {
  const [shouldRender, setShouldRender] = useState(show)
  const [isVisible, setIsVisible] = useState(false)

  // When show becomes true, mount the element; when false, start exit animation
  useEffect(() => {
    if (show) {
      setShouldRender(true)
    } else {
      setIsVisible(false)
    }
  }, [show])

  // After mount/render, trigger the enter animation on the next frame
  useLayoutEffect(() => {
    if (shouldRender && show && !isVisible) {
      requestAnimationFrame(() => {
        setIsVisible(true)
      })
    }
  }, [shouldRender, show, isVisible])

  const handleTransitionEnd = useCallback((e) => {
    // Only respond to transitions on the grid wrapper, not bubbled from children
    if (e.target !== e.currentTarget) return
    if (!show) {
      setShouldRender(false)
    }
  }, [show])

  if (!shouldRender) return null

  return (
    <div
      ref={ref}
      className={cn(className)}
      onTransitionEnd={handleTransitionEnd}
      style={{
        display: "grid",
        gridTemplateRows: isVisible ? "1fr" : "0fr",
        transition: `grid-template-rows ${duration}ms ease, opacity ${duration}ms ease, transform ${duration}ms ease`,
        opacity: isVisible ? 1 : 0,
        transform: isVisible ? "scale(1)" : "scale(0.85)",
      }}
    >
      {/* Inner div collapses via min-height: 0 from the 0fr row.
          Padding + negative margin keep box-shadows visible while
          the overflow boundary still clips correctly during collapse. */}
      <div
        style={{
          overflow: "hidden",
          padding: `${shadowPad}px`,
          margin: `-${shadowPad}px`,
        }}
      >
        {children}
      </div>
    </div>
  )
}

export { AnimatedPresence }
